import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Scan, UploadSimple, FilePdf, FileImage, FileText, PencilSimple, FolderOpen, Scissors, Plus, Trash, StackSimple, ArrowsOutCardinal, TextT, Sparkle,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { dmsApi, STATUS_STYLE, fmtDate, fmtSize } from './dmsApi';
import PageReorderDialog from './PageReorderDialog';
import { folderOptions } from './folderTree';

const icon = (mime, size = 20) => {
  if (mime === 'application/pdf') return <FilePdf size={size} weight="duotone" className="text-rose-500 shrink-0" />;
  if ((mime || '').startsWith('image/')) return <FileImage size={size} weight="duotone" className="text-sky-500 shrink-0" />;
  return <FileText size={size} weight="duotone" className="text-zinc-400 shrink-0" />;
};

const OCR_CHIP = {
  processing: ['認識中…', 'text-amber-700 bg-amber-50 border-amber-200'],
  done: ['読み取り済', 'text-teal-700 bg-teal-50 border-teal-200'],
  failed: ['読み取り失敗', 'text-rose-700 bg-rose-50 border-rose-200'],
};

const fullBody = (d, overrides = {}) => ({
  title: d.title, description: d.description || null, folder_id: d.folder_id,
  doc_type: d.doc_type || 'other', status: d.status, confidentiality: d.confidentiality || 'internal',
  tags: d.tags || [], ...overrides,
});

export default function ScanInboxView({ meta, folders, onOpenDoc, onChanged }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [renameDlg, setRenameDlg] = useState(null);   // {doc, title}
  const [fileDlg, setFileDlg] = useState(null);       // {doc, folderId, docType, status}
  const [splitDlg, setSplitDlg] = useState(null);     // {doc, parts:[{title,pages}], busy}
  const [mergeDlg, setMergeDlg] = useState(null);     // {order:[ids], title, busy}
  const [reorderDoc, setReorderDoc] = useState(null);
  const [aiBusy, setAiBusy] = useState(null);          // doc id being analyzed
  const fileRef = useRef(null);
  const inboxFolder = folders.find((f) => f.seed_key === 'dms_scan_inbox');

  const load = useCallback(async () => {
    setLoading(true);
    try { setDocs(await dmsApi.documents({ tray: 'scan_inbox' })); }
    catch { setDocs([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const intake = async (files) => {
    if (!files?.length) return;
    if (!inboxFolder) { toast.error('受信箱フォルダーが見つかりません'); return; }
    setUploading(true);
    let ok = 0;
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('title', f.name.replace(/\.[^.]+$/, ''));
      fd.append('folder_id', inboxFolder.id);
      fd.append('source_type', 'scan');
      fd.append('status', 'needs_classification');
      try { await dmsApi.upload(fd); ok += 1; }
      catch (e) { toast.error(`${f.name}: ${e?.response?.data?.detail || '取込失敗'}`); }
    }
    if (ok) toast.success(`${ok}件をスキャナー受信箱に取り込みました`);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
    load(); onChanged?.();
  };

  const doRename = async () => {
    if (!renameDlg?.title?.trim()) return;
    try {
      await dmsApi.updateDocument(renameDlg.doc.id, fullBody(renameDlg.doc, { title: renameDlg.title.trim() }));
      toast.success('名前を変更しました'); setRenameDlg(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || '変更に失敗しました'); }
  };

  const doFile = async () => {
    const { doc, folderId, docType, status, title } = fileDlg;
    if (!folderId) { toast.error('フォルダーを選択してください'); return; }
    try {
      await dmsApi.updateDocument(doc.id, fullBody(doc, {
        folder_id: folderId, doc_type: docType, status,
        title: (title || '').trim() || doc.title,
      }));
      toast.success('ファイリングしました'); setFileDlg(null); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'ファイリングに失敗しました'); }
  };

  const doSplit = async () => {
    const parts = splitDlg.parts.filter((p) => p.title.trim() && p.pages.trim());
    if (!parts.length) { toast.error('タイトルとページを入力してください'); return; }
    setSplitDlg((s) => ({ ...s, busy: true }));
    try {
      const r = await dmsApi.splitDocument(splitDlg.doc.id, parts);
      toast.success(`${r.created.length}件に分割しました（元の書類は保持されます）`);
      setSplitDlg(null); load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '分割に失敗しました');
      setSplitDlg((s) => (s ? { ...s, busy: false } : s));
    }
  };

  const canEdit = meta.can_edit;
  const pdfDocs = docs.filter((d) => d.mime_type === 'application/pdf');

  const toggleMergeDoc = (id) => setMergeDlg((s) => {
    const order = s.order.includes(id) ? s.order.filter((x) => x !== id) : [...s.order, id];
    return { ...s, order };
  });

  const doMerge = async () => {
    if (!mergeDlg.title.trim()) { toast.error('結合後のタイトルを入力してください'); return; }
    if (mergeDlg.order.length < 2) { toast.error('2件以上のPDFを選択してください'); return; }
    setMergeDlg((s) => ({ ...s, busy: true }));
    try {
      const r = await dmsApi.mergeDocuments(mergeDlg.title.trim(), mergeDlg.order);
      toast.success(`結合しました: ${r.document_number}（元のPDFは保持されます）`);
      setMergeDlg(null); load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '結合に失敗しました');
      setMergeDlg((s) => (s ? { ...s, busy: false } : s));
    }
  };

  const doSuggest = async (d) => {
    setAiBusy(d.id);
    try {
      const s = await dmsApi.suggestFiling(d.id);
      setFileDlg({
        doc: d, folderId: s.folder_id || '', docType: s.doc_type || 'other',
        status: 'draft', title: s.title || d.title, ai: true,
      });
      toast.success('AI提案を反映しました — 内容を確認して保存してください');
    } catch (e) { toast.error(e?.response?.data?.detail || 'AI提案の取得に失敗しました'); }
    finally { setAiBusy(null); }
  };

  const doOcr = async (d) => {
    try {
      await dmsApi.runOcr(d.id);
      toast.success('文字の読み取りを開始しました — 完了まで少しお待ちください');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '文字の読み取りを開始できませんでした'); }
  };

  return (
    <div className="space-y-3" data-testid="scan-inbox-view">
      <div className="flex items-center gap-2 flex-wrap">
        <Scan size={20} weight="duotone" className="text-amber-600" />
        <h2 className="text-sm font-bold text-zinc-800">スキャナー受信箱</h2>
        <span className="text-[10px] text-zinc-400">{docs.length}件 — ファイリング待ち</span>
        {canEdit && (
          <div className="ml-auto flex items-center gap-1.5">
            {meta.features?.pdf_split_merge && pdfDocs.length >= 2 && (
              <Button size="sm" variant="outline" onClick={() => setMergeDlg({ order: [], title: '', busy: false })}
                className="h-8 text-xs text-orange-700 border-orange-200 hover:bg-orange-50" data-testid="scan-merge-btn">
                <StackSimple size={14} className="mr-1" /> PDF結合
              </Button>
            )}
            <input ref={fileRef} type="file" multiple className="hidden" accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff"
              onChange={(e) => intake(e.target.files)} data-testid="scan-intake-input" />
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="h-8 bg-amber-600 hover:bg-amber-700 text-xs" data-testid="scan-intake-btn">
              <UploadSimple size={14} className="mr-1" /> {uploading ? '取込中…' : 'スキャン取込'}
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="py-12 text-center text-sm text-zinc-400">読み込み中...</p>
      ) : docs.length === 0 ? (
        <div className="py-14 text-center text-zinc-400" data-testid="scan-inbox-empty">
          <Scan size={40} weight="duotone" className="mx-auto mb-2" />
          <p className="text-sm">受信箱は空です — スキャンしたPDFを取り込んでください</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2.5 rounded-lg border border-zinc-200 bg-white px-3 py-2" data-testid={`scan-doc-${d.id}`}>
              {icon(d.mime_type)}
              <button type="button" onClick={() => onOpenDoc(d.id)} className="min-w-0 flex-1 text-left group">
                <span className="block text-[13px] font-medium text-zinc-800 truncate group-hover:text-amber-700 group-hover:underline">{d.title}</span>
                <span className="block text-[10px] text-zinc-400 font-mono">{d.document_number} · {fmtSize(d.file_size)} · {fmtDate(d.created_at)}</span>
              </button>
              <span className={`px-1.5 py-0.5 rounded border text-[9px] font-bold shrink-0 ${STATUS_STYLE[d.status] || ''}`}>{d.status_label}</span>
              {OCR_CHIP[d.ocr_status] && (
                <span className={`px-1.5 py-0.5 rounded border text-[9px] font-bold shrink-0 ${OCR_CHIP[d.ocr_status][1]}`}
                  data-testid={`scan-ocr-chip-${d.id}`}>{OCR_CHIP[d.ocr_status][0]}</span>
              )}
              {canEdit && (
                <span className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="outline" className="h-7 text-[10px] px-2"
                    onClick={() => setRenameDlg({ doc: d, title: d.title })} data-testid={`scan-rename-${d.id}`}>
                    <PencilSimple size={12} className="mr-0.5" /> 名前
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 text-teal-700 border-teal-200 hover:bg-teal-50"
                    onClick={() => setFileDlg({ doc: d, folderId: '', docType: d.doc_type || 'other', status: 'draft', title: d.title })} data-testid={`scan-file-${d.id}`}>
                    <FolderOpen size={12} className="mr-0.5" /> ファイリング
                  </Button>
                  {meta.features?.ai_assist && d.ocr_status === 'done' && (
                    <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 text-violet-700 border-violet-200 hover:bg-violet-50"
                      onClick={() => doSuggest(d)} disabled={aiBusy === d.id} data-testid={`scan-ai-suggest-${d.id}`}>
                      <Sparkle size={12} className="mr-0.5" /> {aiBusy === d.id ? 'AI分析中…' : 'AI提案'}
                    </Button>
                  )}
                  {d.mime_type === 'application/pdf' && meta.features?.pdf_split_merge && (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 text-orange-700 border-orange-200 hover:bg-orange-50"
                        onClick={() => setSplitDlg({ doc: d, parts: [{ title: '', pages: '' }, { title: '', pages: '' }], busy: false })}
                        data-testid={`scan-split-${d.id}`}>
                        <Scissors size={12} className="mr-0.5" /> 分割
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 text-orange-700 border-orange-200 hover:bg-orange-50"
                        onClick={() => setReorderDoc(d)} data-testid={`scan-reorder-${d.id}`}>
                        <ArrowsOutCardinal size={12} className="mr-0.5" /> 並替
                      </Button>
                    </>
                  )}
                  {meta.features?.ocr_text_search && (d.mime_type === 'application/pdf' || (d.mime_type || '').startsWith('image/'))
                    && (!d.ocr_status || d.ocr_status === 'failed') && (
                    <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 text-teal-700 border-teal-200 hover:bg-teal-50"
                      onClick={() => doOcr(d)} data-testid={`scan-ocr-${d.id}`}>
                      <TextT size={12} className="mr-0.5" /> 文字を読み取る
                    </Button>
                  )}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 名前変更 */}
      <Dialog open={!!renameDlg} onOpenChange={(o) => !o && setRenameDlg(null)}>
        <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="scan-rename-dialog">
          <DialogHeader><DialogTitle className="text-base">名前を付ける</DialogTitle></DialogHeader>
          <Input value={renameDlg?.title || ''} onChange={(e) => setRenameDlg((s) => ({ ...s, title: e.target.value }))}
            className="h-9 text-sm" data-testid="scan-rename-input" autoFocus onKeyDown={(e) => e.key === 'Enter' && doRename()} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDlg(null)}>キャンセル</Button>
            <Button onClick={doRename} className="bg-amber-600 hover:bg-amber-700" data-testid="scan-rename-save">保存</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ファイリング */}
      <Dialog open={!!fileDlg} onOpenChange={(o) => !o && setFileDlg(null)}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="scan-file-dialog">
          <DialogHeader><DialogTitle className="text-base">ファイリング — {fileDlg?.doc?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {fileDlg?.ai && (
              <p className="flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-[11px] text-violet-700 font-semibold"
                data-testid="scan-ai-banner">
                <Sparkle size={13} weight="fill" /> AIの提案を反映しています — 修正して保存できます
              </p>
            )}
            <div>
              <label className="text-xs font-semibold text-zinc-600">タイトル</label>
              <Input value={fileDlg?.title || ''} onChange={(e) => setFileDlg((s) => ({ ...s, title: e.target.value }))}
                className="h-9 mt-1 text-sm" data-testid="scan-file-title" />
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-600">保存先フォルダー</label>
              <Select value={fileDlg?.folderId || ''} onValueChange={(v) => setFileDlg((s) => ({ ...s, folderId: v }))}>
                <SelectTrigger className="h-9 mt-1 text-xs" data-testid="scan-file-folder"><SelectValue placeholder="フォルダーを選択" /></SelectTrigger>
                <SelectContent>
                  {folderOptions(folders.filter((f) => !f.is_archive_folder
                    && f.seed_key !== 'dms_scan_inbox')).map((o) => (
                      <SelectItem key={o.value} value={o.value} title={o.pathLabel}>{o.label}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-zinc-600">書類種別</label>
                <Select value={fileDlg?.docType || 'other'} onValueChange={(v) => setFileDlg((s) => ({ ...s, docType: v }))}>
                  <SelectTrigger className="h-9 mt-1 text-xs" data-testid="scan-file-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.doc_types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-600">ステータス</label>
                <Select value={fileDlg?.status || 'draft'} onValueChange={(v) => setFileDlg((s) => ({ ...s, status: v }))}>
                  <SelectTrigger className="h-9 mt-1 text-xs" data-testid="scan-file-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.statuses.map((st) => <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFileDlg(null)}>キャンセル</Button>
            <Button onClick={doFile} className="bg-teal-700 hover:bg-teal-800" data-testid="scan-file-save">
              <FolderOpen size={14} className="mr-1" /> ファイリング
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF分割 */}
      <Dialog open={!!splitDlg} onOpenChange={(o) => !o && setSplitDlg(null)}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="scan-split-dialog">
          <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><Scissors size={17} className="text-orange-600" /> PDF分割 — {splitDlg?.doc?.title}</DialogTitle></DialogHeader>
          <p className="text-[11px] text-zinc-500">ページ範囲（例: 1-3 または 1,3,5）ごとに新しい書類を作成します。元のPDFはそのまま残ります。</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(splitDlg?.parts || []).map((p, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <Input value={p.title} placeholder={`書類${i + 1}のタイトル`}
                  onChange={(e) => setSplitDlg((s) => ({ ...s, parts: s.parts.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) }))}
                  className="h-9 text-sm flex-1" data-testid={`split-title-${i}`} />
                <Input value={p.pages} placeholder="1-3"
                  onChange={(e) => setSplitDlg((s) => ({ ...s, parts: s.parts.map((x, j) => (j === i ? { ...x, pages: e.target.value } : x)) }))}
                  className="h-9 text-sm w-24" data-testid={`split-pages-${i}`} />
                <button type="button" onClick={() => setSplitDlg((s) => ({ ...s, parts: s.parts.filter((_, j) => j !== i) }))}
                  className="text-zinc-300 hover:text-rose-500 shrink-0" data-testid={`split-remove-${i}`}><Trash size={15} /></button>
              </div>
            ))}
          </div>
          <Button size="sm" variant="outline" className="h-8 text-xs w-fit"
            onClick={() => setSplitDlg((s) => ({ ...s, parts: [...s.parts, { title: '', pages: '' }] }))} data-testid="split-add-part">
            <Plus size={13} className="mr-1" /> 分割先を追加
          </Button>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitDlg(null)}>キャンセル</Button>
            <Button onClick={doSplit} disabled={splitDlg?.busy} className="bg-teal-700 hover:bg-teal-800" data-testid="split-save">
              <Scissors size={14} className="mr-1" /> {splitDlg?.busy ? '分割中…' : '分割する'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* PDF結合 */}
      <Dialog open={!!mergeDlg} onOpenChange={(o) => !o && setMergeDlg(null)}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="scan-merge-dialog">
          <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><StackSimple size={17} className="text-orange-600" /> PDF結合</DialogTitle></DialogHeader>
          <p className="text-[11px] text-zinc-500">クリックした順番で結合されます。元のPDFはそのまま残ります。</p>
          <Input value={mergeDlg?.title || ''} onChange={(e) => setMergeDlg((s) => ({ ...s, title: e.target.value }))}
            placeholder="結合後のタイトル" className="h-9 text-sm" data-testid="merge-title-input" />
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {pdfDocs.map((d) => {
              const pos = mergeDlg?.order?.indexOf(d.id) ?? -1;
              return (
                <button key={d.id} type="button" onClick={() => toggleMergeDoc(d.id)}
                  className={`w-full flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors ${
                    pos >= 0 ? 'border-orange-400 bg-orange-50' : 'border-zinc-200 hover:bg-zinc-50'}`}
                  data-testid={`merge-pick-${d.id}`}>
                  <span className={`w-5 h-5 rounded-full text-[10px] font-black flex items-center justify-center shrink-0 ${
                    pos >= 0 ? 'bg-orange-600 text-white' : 'bg-zinc-100 text-zinc-300'}`}>{pos >= 0 ? pos + 1 : '·'}</span>
                  {icon(d.mime_type, 16)}
                  <span className="min-w-0 flex-1">
                    <span className="block text-[12px] font-medium text-zinc-800 truncate">{d.title}</span>
                    <span className="block text-[9px] text-zinc-400 font-mono">{d.document_number} · {fmtSize(d.file_size)}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDlg(null)}>キャンセル</Button>
            <Button onClick={doMerge} disabled={mergeDlg?.busy} className="bg-teal-700 hover:bg-teal-800" data-testid="merge-save">
              <StackSimple size={14} className="mr-1" /> {mergeDlg?.busy ? '結合中…' : `結合する（${mergeDlg?.order?.length || 0}件）`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {reorderDoc && (
        <PageReorderDialog doc={reorderDoc} onClose={() => setReorderDoc(null)}
          onDone={() => { load(); onChanged?.(); }} />
      )}
    </div>
  );
}
